import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, fetchJson } from "@/lib/admin-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** POS2-1333: one old-platform user on a site, and whether anything is left to do for them. */
interface ReconciledUser {
  email: string;
  name: string;
  /** The old system's own role text. Information only — the mapping onto our roles is not
   *  reliable, which is why the row carries a picker rather than a suggestion. */
  old_role: string | null;
  status: "member" | "invited" | "missing" | "inactive";
  membership_inactive: boolean;
  invited_at: string | null;
  /** POS2-1309: an unspent hash means this person signs in with their old password. */
  carry_over_available: boolean;
  sites: SiteGrant[];
  sites_not_imported: number;
}

/** One of this person's old sites that exists in FoodSight. Inactive ones are never grantable. */
interface SiteGrant {
  id: string;
  name: string;
  grantable: boolean;
  reason: string | null;
}

interface OrgRole {
  id: string;
  name: string;
}

interface Reconciliation {
  users: ReconciledUser[];
  roles: OrgRole[];
  /** When the old users were last snapshotted on DataBridge (POS2-1383); null is never. */
  snapshot_taken_at: string | null;
}

interface InviteOutcome {
  email: string;
  result: "invited" | "skipped_member" | "skipped_invited" | "failed";
  email_sent: boolean;
  detail: string | null;
}

interface InviteUsersResult {
  outcomes: InviteOutcome[];
}

/**
 * POS2-1333: who from the old platform is still missing from this site, and inviting them.
 *
 * **Moved to the Sites page by POS2-1383**, keyed on the FoodSight site and read from the users
 * snapshot, so it keeps working after the old database is gone.
 *
 * **Replaces the user importer.** That importer created accounts directly and has never run in
 * production — people arrive by invitation and register themselves — so two actions side by side
 * only invited the wrong press.
 *
 * **The site card is an entry point, not a scope.** An invitation grants every site that person
 * had in the old system, of those already imported, so inviting from any one of their cards is
 * enough. Where that is fewer sites than they actually had, the row says so rather than granting
 * a subset silently.
 *
 * **Roles are chosen, never suggested.** The old role text does not map onto our three roles
 * reliably, so it sits beside the picker as information. A row with no role chosen cannot be
 * sent: a default here would grant real access to whoever was left unlooked-at.
 */
export function InviteUsersDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  siteId: string;
  siteName: string;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Record<string, string>>({});
  // Per-address site choice, absent until somebody narrows it. Absent means "every grantable site
  // they had", which is what the server does with a missing list — so the common case sends
  // nothing and cannot drift from the server's own default.
  const [sites, setSites] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const usersUrl = `${API_URL}/api/v1/admin/sites/${siteId}/users`;
  const listQuery = useQuery<Reconciliation, Error>({
    queryKey: ["admin", "sites", siteId, "reconciled-users"],
    queryFn: () => fetchJson<Reconciliation>(`${usersUrl}/reconciled`, headers),
    enabled: open,
    retry: false,
  });

  const inviteMutation = useMutation<InviteUsersResult, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${usersUrl}/invite`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          selections: Object.entries(selected).map(([email, role_id]) => ({
            email,
            role_id,
            ...(sites[email] ? { site_ids: sites[email] } : {}),
          })),
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = (await res.json()) as { data: InviteUsersResult };
      return json.data;
    },
    onSuccess: () => {
      setSelected({});
      setSites({});
      // Re-read rather than patch the rows we sent: the answer for each address is the server's,
      // and somebody else may have been working the same site.
      queryClient.invalidateQueries({ queryKey: ["admin", "sites", siteId, "reconciled-users"] });
    },
    onError: (err) => setSubmitError(err.message),
  });

  const reset = inviteMutation.reset;
  useEffect(() => {
    if (!open) {
      setSelected({});
      setSites({});
      setSubmitError(null);
      reset();
    }
  }, [open, reset]);

  const users = listQuery.data?.users ?? [];
  const roles = listQuery.data?.roles ?? [];
  const invitable = users.filter((u) => u.status === "missing");
  const chosen = Object.entries(selected);
  // Every selected row needs a role. Anything else would send somebody an access level nobody
  // picked, which is the one mistake here that is not recoverable by trying again.
  const readyToSend = chosen.length > 0 && chosen.every(([, role]) => role !== "");

  function toggle(email: string) {
    setSelected((prev) => {
      const next = { ...prev };
      if (email in next) delete next[email];
      // No role by default: it is chosen in the row. An empty string is "selected, still needs
      // one", which is what keeps Send disabled until every selected row has been decided.
      else next[email] = "";
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invite Users</DialogTitle>
          <DialogDescription>
            {siteName} — invite the old-platform users who are not on FoodSight yet. One send per
            person covers every site of theirs we have imported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          {listQuery.isLoading && <Skeleton className="h-48 w-full" />}
          {listQuery.isError && (
            <p className="text-xs text-destructive">{listQuery.error.message}</p>
          )}

          {listQuery.data && listQuery.data.snapshot_taken_at === null && (
            <p className="text-xs text-amber-700">
              The old platform&apos;s users have not been snapshotted yet. Take the snapshot on
              DataBridge (Snapshot legacy users) while the old database is still up.
            </p>
          )}
          {listQuery.data && listQuery.data.snapshot_taken_at !== null && (
            <p className="text-xs text-muted-foreground">
              Old-platform users as of{" "}
              {new Date(listQuery.data.snapshot_taken_at).toLocaleString("en-GB", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
              .
            </p>
          )}

          {listQuery.data && users.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No users have access to this site in the old system.
            </p>
          )}

          {listQuery.data && users.length > 0 && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground">
                  {invitable.length} to invite · {users.length} with access in the old system
                </span>
                {invitable.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      setSelected(Object.fromEntries(invitable.map((u) => [u.email, ""])))
                    }
                  >
                    Select all
                  </Button>
                )}
              </div>

              <div className="max-h-[22rem] divide-y overflow-y-auto rounded border">
                {users.map((u) => (
                  <UserRow
                    key={u.email}
                    user={u}
                    roles={roles}
                    selectedRole={selected[u.email]}
                    chosenSites={sites[u.email]}
                    onToggle={() => toggle(u.email)}
                    onRole={(roleId) => setSelected((prev) => ({ ...prev, [u.email]: roleId }))}
                    onSites={(ids) => setSites((prev) => ({ ...prev, [u.email]: ids }))}
                  />
                ))}
              </div>
            </>
          )}

          {inviteMutation.data && (
            <div className="space-y-1 rounded border bg-muted/30 p-3 text-xs">
              <p className="font-medium">
                {inviteMutation.data.outcomes.filter((o) => o.result === "invited").length} invited
              </p>
              {inviteMutation.data.outcomes
                .filter((o) => o.result !== "invited")
                .map((o) => (
                  <p key={o.email} className="text-muted-foreground">
                    {o.email} —{" "}
                    {o.result === "skipped_member"
                      ? "already a member"
                      : o.result === "skipped_invited"
                        ? "already invited"
                        : (o.detail ?? "failed")}
                  </p>
                ))}
              {inviteMutation.data.outcomes.some(
                (o) => o.result === "invited" && !o.email_sent,
              ) && (
                <p className="text-destructive">
                  Some invitations were created but their email did not send — the rows exist, so a
                  resend is all that is needed.
                </p>
              )}
            </div>
          )}

          {submitError && <p className="text-xs text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              setSubmitError(null);
              inviteMutation.mutate();
            }}
            disabled={!readyToSend || inviteMutation.isPending}
          >
            {inviteMutation.isPending
              ? "Sending…"
              : `Send ${chosen.length || ""} invitation${chosen.length === 1 ? "" : "s"}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One row. Only `missing` is actionable; everything else states why it is not.
 *
 * **Two lines, not one.** The notes go under the address rather than beside the role picker. On
 * one line the right-hand cluster grew with the number of notes and pushed the picker out of the
 * dialog; below the address it cannot, however many notes a person collects.
 *
 * **The sites are named, and narrowing them is a click away.** A count alone does not say *which*
 * sites somebody is about to be given, which is the thing an operator needs to know before
 * sending. Inactive sites appear in the list with their reason and no checkbox: a customer who
 * shut a site down should not have somebody handed access to it by an invitation nobody read
 * closely.
 */
function UserRow({
  user,
  roles,
  selectedRole,
  chosenSites,
  onToggle,
  onRole,
  onSites,
}: {
  user: ReconciledUser;
  roles: OrgRole[];
  selectedRole: string | undefined;
  chosenSites: string[] | undefined;
  onToggle: () => void;
  onRole: (roleId: string) => void;
  onSites: (ids: string[]) => void;
}) {
  const [showSites, setShowSites] = useState(false);
  const actionable = user.status === "missing";
  const grantable = user.sites.filter((s) => s.grantable);
  // Absent means the server's own default: every grantable site. Only a deliberate narrowing is
  // held here, so the two cannot disagree about what "all of them" means.
  const granting = chosenSites ?? grantable.map((s) => s.id);

  function toggleSite(id: string) {
    const next = granting.includes(id) ? granting.filter((s) => s !== id) : [...granting, id];
    onSites(next);
  }

  return (
    // `data-email` is a test anchor rather than styling: the tests used to find a row by its
    // class list, so changing the layout broke nine of them without any behaviour changing.
    <div className="px-3 py-2" data-email={user.email}>
      <div className="flex items-start gap-3">
        <div className="flex w-5 shrink-0 justify-center pt-1">
          {actionable ? (
            <Checkbox checked={selectedRole !== undefined} onCheckedChange={onToggle} />
          ) : user.status === "member" ? (
            <span
              className="text-green-600"
              title={user.membership_inactive ? "Member, but deactivated" : "Already has access"}
            >
              ✓
            </span>
          ) : user.status === "invited" ? (
            <span className="text-muted-foreground" title="Invitation already sent">
              ◔
            </span>
          ) : (
            <span className="text-muted-foreground" title="Inactive in the old system">
              —
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium">{user.name || user.email}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {user.old_role && <span>was {user.old_role}</span>}
            {user.status === "member" && user.membership_inactive && <span>deactivated</span>}
            {user.status === "invited" && user.invited_at && (
              <span>invited {new Date(user.invited_at).toLocaleDateString()}</span>
            )}
            {user.status === "inactive" && <span>left</span>}
            {actionable && user.carry_over_available && (
              <Badge variant="outline" className="text-xs font-normal">
                old password works
              </Badge>
            )}
            {actionable && user.sites.length > 0 && (
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => setShowSites((v) => !v)}
              >
                {granting.length} of {user.sites.length + user.sites_not_imported} sites
              </button>
            )}
            {actionable && user.sites.length === 0 && user.sites_not_imported > 0 && (
              <span title="None of their sites have been imported yet">
                no sites yet ({user.sites_not_imported} not imported)
              </span>
            )}
          </div>

          {actionable && showSites && (
            <div className="mt-2 space-y-1 rounded border bg-muted/20 p-2">
              {user.sites.map((site) => (
                <label
                  key={site.id}
                  className="flex items-center gap-2 text-xs"
                  title={site.grantable ? undefined : `Not available: ${site.reason}`}
                >
                  <Checkbox
                    checked={granting.includes(site.id)}
                    disabled={!site.grantable}
                    onCheckedChange={() => toggleSite(site.id)}
                  />
                  <span className={site.grantable ? "" : "text-muted-foreground line-through"}>
                    {site.name}
                  </span>
                  {!site.grantable && <span className="text-muted-foreground">{site.reason}</span>}
                </label>
              ))}
              {user.sites_not_imported > 0 && (
                <p className="pt-1 text-xs text-muted-foreground">
                  {user.sites_not_imported} more not imported yet — an invitation cannot grant
                  those.
                </p>
              )}
            </div>
          )}
        </div>

        {actionable && (
          <div className="w-32 shrink-0">
            <Select value={selectedRole ?? ""} onValueChange={onRole}>
              <SelectTrigger className="h-7 w-full text-xs">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
